/**
 * 🟦 V11.4 — Captura visual del nodo seleccionado.
 *
 * Usa el iframe del Builder + html2canvas (lazy import) para obtener
 * un PNG base64 (max 512×512) y los computed styles del browser.
 * No remountea el iframe ni altera su DOM.
 */
import { getBuilderIframe } from '../lyonBridge';

const MAX_DIM = 512;

const KEYS = [
  'color', 'backgroundColor', 'borderRadius', 'boxShadow', 'fontSize',
  'fontWeight', 'fontFamily', 'padding', 'margin', 'opacity',
  'backdropFilter', 'filter', 'border', 'gap', 'display',
  'flexDirection', 'justifyContent', 'alignItems', 'gridTemplateColumns',
  'textAlign', 'lineHeight', 'letterSpacing',
];

export async function captureNodeImage(uibId) {
  if (!uibId) return { error: 'no_uib_id' };
  const iframeEl = getBuilderIframe();
  const win = iframeEl?.contentWindow;
  const doc = iframeEl?.contentDocument;
  if (!win || !doc) return { error: 'iframe_unavailable' };
  const target = doc.querySelector(`[data-uib-id="${uibId}"]`);
  if (!target) return { error: 'node_not_found' };

  // Computed styles primero (rápido, sin dependencias).
  const computed = win.getComputedStyle(target);
  const computed_styles = {};
  for (const k of KEYS) {
    try { computed_styles[k] = computed[k]; } catch { /* ignore */ }
  }

  // html2canvas dinámico (lazy). Tolerante a fallos: si no se puede
  // capturar, devolvemos solo computed_styles.
  let imageB64 = null;
  try {
    const mod = await import('html2canvas');
    const h2c = mod.default || mod;
    const rect = target.getBoundingClientRect();
    const scale = Math.min(2, MAX_DIM / Math.max(rect.width || 1, rect.height || 1, 1));
    const canvas = await Promise.race([
      h2c(target, { backgroundColor: null, scale, useCORS: true, allowTaint: true, logging: false }),
      new Promise((resolve) => setTimeout(() => resolve(null), 5000)),
    ]);
    if (canvas) {
      // Re-escalar a max 512×512 si quedó más grande.
      const w = Math.min(canvas.width, MAX_DIM);
      const h = Math.min(canvas.height, MAX_DIM);
      const out = doc.createElement('canvas');
      out.width = w; out.height = h;
      out.getContext('2d').drawImage(canvas, 0, 0, w, h);
      const dataUrl = out.toDataURL('image/png');
      const i = dataUrl.indexOf('base64,');
      imageB64 = i >= 0 ? dataUrl.slice(i + 7) : null;
    }
  } catch { /* fallback: sin imagen */ }

  return { imageB64, computed_styles };
}
